import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import MarketingNav from '../components/MarketingNav';
import Footer from '../components/Footer';
import './landing.css';
import { webPlanPrices } from '../lib/storePricing';

const FEATURES = [
  {
    glyph: '◆',
    title: 'Twelve agents, one feed',
    body: 'Spending, portfolio, market, budgets, debt, taxes, and more — analyzed in parallel, then ranked so you see what matters first.',
  },
  {
    glyph: '⇄',
    title: 'Cross-domain signals',
    body: 'The connection between how you spend and how you invest — surfaced. You shop there every week; do you own it?',
  },
  {
    glyph: '◷',
    title: 'Automated trade signals',
    body: 'Rule-based strategies flag trades on your account, paper or live — you confirm before anything executes. A one-tap kill switch stops everything, instantly.',
  },
  {
    glyph: '✦',
    title: 'AI on call',
    body: 'Chat with cross-session memory that already knows your accounts, goals, and automations.',
  },
  {
    glyph: '◐',
    title: 'Budgets & goals',
    body: 'Set budgets, track savings goals, and get alerted before you overspend — not after.',
  },
  {
    glyph: '⌂',
    title: 'Built for your household',
    body: 'A shared dashboard with combined goals and contribution tracking — see your finances together, with a partner or the whole household, without merging everything.',
  },
];

const DIFFERENTIATORS = [
  {
    n: '01',
    title: 'Parallel, not sequential',
    body: 'All twelve agents run simultaneously. By the time you open the app, the analysis across spending, investing, budgets, and risk is already done.',
  },
  {
    n: '02',
    title: 'Spending and investing, together',
    body: "Signals that only emerge at the intersection — like holding stock in a brand you spend heavily at — don't surface when the two are analyzed separately.",
  },
  {
    n: '03',
    title: 'A ranked feed, not more numbers',
    body: "You don't need a bigger dashboard. You need to know what's urgent and what can wait. One prioritized list; the most important insight at the top.",
  },
  {
    n: '04',
    title: 'Memory across sessions',
    body: 'The AI already knows your accounts, goals, and automations. Every conversation continues with your full financial picture in context — nothing re-explained from scratch.',
  },
];

const STEPS = [
  { n: '01', title: 'Connect', body: 'Link your bank through Plaid, and optionally a paper brokerage account.' },
  { n: '02', title: 'Analyze', body: 'Agents run in parallel across your full financial picture; an LLM judge synthesizes and ranks the output.' },
  { n: '03', title: 'Act', body: 'Read one prioritized feed, set goals and budgets, or let autopilot trade on paper — with you in control.' },
];

const TIERS = [
  { name: 'Free', price: '$0', note: 'Banking insights to start', points: ['Bank connection + expenses', 'Banking AI insights', '10 AI chat messages/day', 'Connect your brokerage — manual trading'] },
  { name: 'Plus', price: webPlanPrices().monthly.priceString, period: '/mo', note: 'The full intelligence layer', points: ['All 12 AI agents · 3 refreshes/day', '30 AI chat messages/day', 'Trading automations + paper autopilot', 'Multiple accounts + household'], featured: true },
];

const BLOG_LINKS = [
  { title: 'The Best Budgeting Apps That Also Track Your Investments', href: '/blog/budgeting-apps-that-track-investments/', body: 'What to actually look for in a budgeting app that sees your brokerage account too.' },
  { title: 'Spending Habits vs. Portfolio Risk', href: '/blog/spending-vs-portfolio-risk/', body: 'Your spending and your portfolio look like separate risk profiles. They’re not.' },
  { title: 'How to Build a Budget That Accounts for Your Investments', href: '/blog/budget-that-accounts-for-investments/', body: 'A budget that treats investing as part of the plan, not an afterthought.' },
];

const TRUST = [
  { title: 'You confirm every trade', body: 'Autopilot flags a trade and waits for your approval — nothing executes automatically, whether your account is paper or live.' },
  { title: 'You stay in control', body: 'A global kill switch halts every automation in one tap.' },
  { title: 'Security you can name', body: 'Bank links go through Plaid — your credentials never touch Agence. Encrypted at rest (AES-256-GCM), 2FA, and TLS in transit.' },
  { title: 'Your data isn’t sold', body: 'Connections power your insights — nothing more.' },
];

export default function Landing() {
  const rootRef = useRef(null);

  // Scroll-reveal: add `.lp-reveal--in` as elements enter the viewport.
  // Under prefers-reduced-motion, CSS keeps elements visible and skips the transition.
  useEffect(() => {
    const root = rootRef.current;
    const els = root?.querySelectorAll('.lp-reveal') || [];
    if (!('IntersectionObserver' in window) || els.length === 0) return;
    // Arm the page only once JS + IO are available — CSS keys the hidden state
    // off `.lp-js`, so without this class all content stays visible (crawlers, no-JS).
    root.classList.add('lp-js');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('lp-reveal--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp" ref={rootRef}>
      <PageMeta
        titleOverride="AI Budgeting App With Investment Tracking | Agence"
        description="Budgeting + investment tracking, plus rule-based trade automation you control — test it risk-free in a paper-trading sandbox with an instant kill switch."
        path="/"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              '@id': 'https://agence.finance/#website',
              name: 'Agence',
              url: 'https://agence.finance',
              potentialAction: {
                '@type': 'SearchAction',
                target: { '@type': 'EntryPoint', urlTemplate: 'https://agence.finance/dashboard?q={search_term_string}' },
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type': 'Organization',
              '@id': 'https://agence.finance/#organization',
              name: 'Agence',
              url: 'https://agence.finance',
              logo: { '@type': 'ImageObject', url: 'https://agence.finance/agence-icon-sharpened.png' },
              sameAs: [],
            },
            {
              '@type': ['SoftwareApplication', 'WebApplication'],
              '@id': 'https://agence.finance/#app',
              name: 'Agence',
              applicationCategory: 'FinanceApplication',
              operatingSystem: 'Web',
              url: 'https://agence.finance',
              description: 'AI budgeting app with investment tracking and rule-based trade automation. Set your own automation rules and test them risk-free in a paper-trading sandbox with an instant kill switch.',
              publisher: { '@id': 'https://agence.finance/#organization' },
              offers: [
                { '@type': 'Offer', price: '0', priceCurrency: 'USD', name: 'Free' },
                { '@type': 'Offer', price: '12', priceCurrency: 'USD', name: 'Plus' },
              ],
            },
            {
              '@type': 'FinancialProduct',
              '@id': 'https://agence.finance/#product',
              name: 'Agence — AI Finance Copilot',
              url: 'https://agence.finance',
              description: 'Connects bank accounts and investment accounts. AI agents surface cross-domain insights across spending, portfolio health, goals, and market context.',
              feesAndCommissionsSpecification: 'Free to use. Every automated trade requires your confirmation before it executes, on paper or live accounts.',
              provider: { '@id': 'https://agence.finance/#organization' },
            },
          ],
        }}
      />

      <MarketingNav />

      <main>
        {/* Hero */}
        <section className="lp-container lp-hero" aria-labelledby="lp-hero-heading">
          <div className="lp-hero-copy lp-reveal">
            <p className="lp-eyebrow">AI finance &amp; investment copilot</p>
            <h1 id="lp-hero-heading" className="lp-h1">Your spending and investing,<br />finally on the same page.</h1>
            <p className="lp-lead">
              Twelve AI agents analyze your full financial life in parallel and surface
              the few things that actually need your attention — in one prioritized feed.
            </p>
            <div className="lp-cta-row">
              <Link to="/register" className="lp-btn lp-btn--primary lp-btn--lg">Get started free</Link>
              <Link to="/pricing" className="lp-btn lp-btn--ghost lp-btn--lg">See pricing →</Link>
            </div>
            <p className="lp-trustline">You confirm every trade · bank links secured by Plaid · always in your control</p>
          </div>

          {/* CSS insight-feed mock — depth without an image asset */}
          <div className="lp-hero-visual lp-reveal" aria-hidden="true">
            <div className="lp-mock">
              <div className="lp-mock-head">
                <span className="lp-mock-title">Today’s feed</span>
                <span className="lp-mock-tag">12 agents</span>
              </div>
              <div className="lp-mock-row">
                <span className="lp-dot lp-dot--high" />
                <span className="lp-mock-text"><strong>Portfolio breakdown</strong> — your top 3 holdings are 68% of total</span>
              </div>
              <div className="lp-mock-row">
                <span className="lp-dot lp-dot--med" />
                <span className="lp-mock-text"><strong>Dining up 41%</strong> month over month</span>
              </div>
              <div className="lp-mock-row">
                <span className="lp-dot lp-dot--med" />
                <span className="lp-mock-text"><strong>You spend at Amazon</strong> — and you own it</span>
              </div>
              <div className="lp-mock-row">
                <span className="lp-dot lp-dot--low" />
                <span className="lp-mock-text"><strong>Goal on pace</strong> — emergency fund 78%</span>
              </div>
              <div className="lp-mock-bars" aria-hidden="true">
                <span style={{ height: '38%' }} /><span style={{ height: '62%' }} />
                <span style={{ height: '49%' }} /><span style={{ height: '80%' }} />
                <span style={{ height: '57%' }} /><span style={{ height: '71%' }} />
              </div>
            </div>
          </div>
        </section>

        {/* The core insight */}
        <section className="lp-insight">
          <div className="lp-container lp-reveal">
            <p className="lp-insight-text">
              Your bank app and your brokerage don’t talk to each other.<br />
              The connection between how you <em>spend</em> and how you <em>invest</em><br />
              is something no product surfaces.<br />
              <span className="lp-insight-accent">Agence does.</span>
            </p>
          </div>
        </section>

        {/* Features — bento */}
        <section className="lp-container lp-section" aria-labelledby="lp-features-heading">
          <p className="lp-section-eyebrow">What you get</p>
          <h2 id="lp-features-heading" className="lp-section-title lp-section-title--oneline">One copilot for your whole financial life</h2>
          <div className="lp-bento">
            {FEATURES.map((f) => (
              <article key={f.title} className={`lp-card lp-reveal${f.wide ? ' lp-card--wide' : ''}`}>
                <span className="lp-card-glyph" aria-hidden="true">{f.glyph}</span>
                <h3 className="lp-card-title">{f.title}</h3>
                <p className="lp-card-body">{f.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Differentiation */}
        <section className="lp-diff" aria-labelledby="lp-diff-heading">
          <div className="lp-container lp-diff-inner">
            <div className="lp-diff-statement lp-reveal">
              <p className="lp-section-eyebrow">Why it's different</p>
              <h2 id="lp-diff-heading" className="lp-diff-title">Built around the gap everyone else left open.</h2>
              <p className="lp-diff-body">
                Most tools give you a slice of your finances and a dashboard to interpret.
                Agence runs twelve specialized agents across your full picture — simultaneously —
                and synthesizes the output into the few things that actually need your attention.
              </p>
            </div>
            <div className="lp-diff-list">
              {DIFFERENTIATORS.map((d) => (
                <div key={d.n} className="lp-diff-item lp-reveal">
                  <span className="lp-diff-num" aria-hidden="true">{d.n}</span>
                  <div>
                    <h3 className="lp-diff-item-title">{d.title}</h3>
                    <p className="lp-diff-item-body">{d.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="lp-container lp-section" aria-labelledby="lp-how-heading">
          <p className="lp-section-eyebrow">How it works</p>
          <h2 id="lp-how-heading" className="lp-section-title">Connect, analyze, act</h2>
          <div className="lp-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="lp-step lp-reveal">
                <span className="lp-step-num">{s.n}</span>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-body">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing teaser */}
        <section className="lp-container lp-section" aria-labelledby="lp-pricing-heading">
          <p className="lp-section-eyebrow">Pricing</p>
          <h2 id="lp-pricing-heading" className="lp-section-title lp-section-title--oneline">Try Plus free for 7 days. Upgrade when you're ready for the full picture.</h2>
          <div className="lp-pricing">
            {TIERS.map((t) => (
              <article key={t.name} className={`lp-tier lp-reveal${t.featured ? ' lp-tier--featured' : ''}`}>
                {t.featured && <span className="lp-tier-badge">Most popular</span>}
                <h3 className="lp-tier-name">{t.name}</h3>
                <p className="lp-tier-price"><span className="lp-price">{t.price}</span>{t.period && <span className="lp-period">{t.period}</span>}</p>
                <p className="lp-tier-note">{t.note}</p>
                <ul className="lp-tier-points">
                  {t.points.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </article>
            ))}
          </div>
          <div className="lp-pricing-cta">
            <Link to="/pricing" className="lp-btn lp-btn--ghost">Compare all features →</Link>
          </div>
        </section>

        {/* Trust / safety */}
        <section className="lp-container lp-section" aria-labelledby="lp-trust-heading">
          <p className="lp-section-eyebrow">Built carefully</p>
          <h2 id="lp-trust-heading" className="lp-section-title lp-section-title--oneline">Designed to keep you in control</h2>
          <div className="lp-trust">
            {TRUST.map((t) => (
              <div key={t.title} className="lp-trust-item lp-reveal">
                <h3 className="lp-trust-title">{t.title}</h3>
                <p className="lp-trust-body">{t.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* From the blog */}
        <section className="lp-container lp-section" aria-labelledby="lp-blog-heading">
          <p className="lp-section-eyebrow">From the blog</p>
          <h2 id="lp-blog-heading" className="lp-section-title lp-section-title--oneline">Budgeting and investing, read together</h2>
          <div className="lp-trust">
            {BLOG_LINKS.map((b) => (
              <a
                key={b.href}
                href={b.href}
                className="lp-trust-item lp-reveal"
                style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
              >
                <h3 className="lp-trust-title">{b.title}</h3>
                <p className="lp-trust-body">{b.body}</p>
              </a>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="lp-cta-band">
          <div className="lp-container lp-reveal">
            <h2 className="lp-cta-title">See your whole financial picture.</h2>
            <p className="lp-cta-sub">Free to start. No card required.</p>
            <div className="lp-cta-row lp-cta-row--center">
              <Link to="/register" className="lp-btn lp-btn--primary lp-btn--lg">Get started free</Link>
              <Link to="/login" className="lp-btn lp-btn--text">I already have an account</Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
